import { Api } from '@/api/Api'
import { ActionTree } from 'vuex'
import RootState, { Category, EntityLevel, ManufacturingSeries, None, Product, ProductClass } from '../types'
import State from './types'
import { Topology } from '../editor/types'
import SecurityState from '../security/types'

const api = new Api()

export const actions: ActionTree<State, RootState> = {
  getTopology(
    {
      state: { activeItem, originalItem },
      rootState,
      dispatch,
      commit,
      getters: { realTopologyFromActive },
    },
    {
      reloadEntityLevel,
      reloadActiveEntityLevel,
    }: { reloadEntityLevel: EntityLevel; reloadActiveEntityLevel: boolean },
  ): Promise<any> {
    dispatch('setTreeLoading', { value: true })
    const productFinderState = (rootState as any)['product-finder']

    // Q: should rootActiveTopology be current?
    const {
      categoryOpen,
      productClassOpen,
      manufacturingSeriesOpen,
      activeItem: rootActiveItem,
      activeTopology: rootActiveTopology,
    } = productFinderState

    // TODO: rootActiveItem is what it is on "reload"
    if (!activeItem || activeItem.isNone) {
      // shallow version of the activeItem in the state - will be reloaded anyway
      // has just enough to reload it
      // getActiveItem?
      activeItem = rootActiveItem

      // reload the real version from the shallow version that's in the order-state
      // Note: the activeTopology will be set by the set[...]Actives...
      switch (activeItem.entityLevel) {
        case EntityLevel.Category:
          dispatch('setCategoryActive', { category: rootActiveItem })
          break
        case EntityLevel.ProductClass:
          dispatch('setProductClassActive', {
            category: rootActiveTopology.category,
            productClass: rootActiveItem,
          })
          break
        case EntityLevel.ManufacturingSeries:
          dispatch('setManufacturingSeriesActive', {
            category: rootActiveTopology.category,
            productClass: rootActiveTopology.productClass,
            manufacturingSeries: rootActiveItem,
          })
          break
        case EntityLevel.Product:
          dispatch('setProductActive', {
            category: rootActiveTopology.category,
            productClass: rootActiveTopology.productClass,
            manufacturingSeries: rootActiveTopology.manufacturingSeries,
            product: rootActiveItem,
          })
          break
      }
    }

    if (reloadActiveEntityLevel) {
      reloadEntityLevel = activeItem.entityLevel
    }

    const url = `${rootState.apiPrefix ?? ''}/modelfinder/VisibleCategories`
    const securityState = (this.state as any).security as SecurityState
    return api
      .fetchV2({
        url,
        user: securityState.currentUser,
      })
      .then((response) => {
        let item:
          | Category
          | ProductClass
          | ManufacturingSeries
          | Product
          | None
          | undefined
        if (typeof reloadEntityLevel === 'undefined' || reloadEntityLevel < 0) {
          item = rootActiveItem // note: this is a shallow item now, but will get replaced below
          const categories: Category[] = response.List

          // fill in all the missing open states. This may seem consuming but Vue has an issue with tracking the undefined ones
          categories.forEach((category: Category) => {
            category.open = !!categoryOpen[category.CategoryId]
            category.ProductClasses.forEach((productClass: ProductClass) => {
              productClass.open =
                !!productClassOpen[productClass.ProductClassId]
              productClass.ManufacturingSeries.forEach(
                (manufacturingSeries: ManufacturingSeries) => {
                  manufacturingSeries.open =
                    !!manufacturingSeriesOpen[
                      manufacturingSeries.ManufacturingSeriesId
                    ]
                },
              )
            })
          })
          commit('treeTopologyLoaded', { categories })
        } else {
          // TODO: I need to do some of this ANYWAY... right?
          // No -
          const updateEntityLevel: EntityLevel =
            reloadEntityLevel as EntityLevel
          // reloadEntityLevel = reloadEntityLevel as EntityLevel // why do I need to do this? it's SO WEIRD
          // reloadEntityLevel
          // note: can't add new Category, yet... so we don't need that "item"
          switch (
            updateEntityLevel // we don't have time for breaks!
          ) {
            case EntityLevel.All: {
              const categories: Category[] = response.List

              // note: yes, we are repeasting this in our cases, for now
              item = categories.find(
                (c) => c.CategoryId === (originalItem as Category).CategoryId,
              )
              commit('updateTopologyItem', {
                topology: realTopologyFromActive,
                category: item,
              })
              break
            }
            case EntityLevel.Category: {
              const category: Category = response.Data

              // note: yes, we are repeasting this in our cases, for now
              category.open = !!categoryOpen[category.CategoryId]
              category.ProductClasses.forEach((pc) => {
                pc.open = !!productClassOpen[pc.ProductClassId]
                pc.ManufacturingSeries.forEach((ms) => {
                  ms.open = !!manufacturingSeriesOpen[ms.ManufacturingSeriesId]
                })
              })

              commit('updateTopologyItem', {
                topology: realTopologyFromActive,
                category,
              })
              if (activeItem.entityLevel > reloadEntityLevel) {
                item = category.ProductClasses.find(
                  (c) =>
                    c.ProductClassId ===
                    (originalItem as ProductClass).ProductClassId,
                )
              } else {
                item = category
              }
              break
            }
            case EntityLevel.ProductClass: {
              // note: yes, we are repeasting this in our cases, for now
              const productClass: ProductClass = response.Data
              productClass.open =
                !!productClassOpen[productClass.ProductClassId]
              productClass.ManufacturingSeries.forEach((ms) => {
                ms.open = !!manufacturingSeriesOpen[ms.ManufacturingSeriesId]
              })

              commit('updateTopologyItem', {
                topology: realTopologyFromActive,
                productClass,
              })
              if (activeItem.entityLevel > reloadEntityLevel) {
                item = productClass.ManufacturingSeries.find(
                  (c) =>
                    c.ManufacturingSeriesId ===
                    (originalItem as ManufacturingSeries).ManufacturingSeriesId,
                )
              } else {
                item = productClass
              }
              break
            }
            case EntityLevel.ManufacturingSeries: {
              const manufacturingSeries: ManufacturingSeries = response.Data

              manufacturingSeries.open =
                !!manufacturingSeriesOpen[
                  manufacturingSeries.ManufacturingSeriesId
                ]

              commit('updateTopologyItem', {
                topology: realTopologyFromActive,
                manufacturingSeries,
              })
              if (activeItem.entityLevel > reloadEntityLevel) {
                item = manufacturingSeries.Products.find(
                  (c) => c.ProductId === (originalItem as Product).ProductId,
                )
              } else {
                item = manufacturingSeries
              }
              break
            }
            case EntityLevel.Product: {
              const product: Product = response.Data
              commit('updateTopologyItem', {
                topology: realTopologyFromActive,
                product,
              })
              if (activeItem.entityLevel > reloadEntityLevel) {
                // can't be Model, so...
              } else {
                item = product
              }
              break
            }
          }
        }

        // TODO: needed?
        // commit('setActiveItem', activeItem) // set the active topology to the active one
        // setCategoryActive?
        // set the activeItem to be the activeItem - because there's all sorts of loading involved when you do.

        // the originalItem is no longer VALID - this what we have TO UPDATE
        // note: I thought realTopology would be best, but it's not 100% correct for some reason?

        // TODO: why is this -1? ProductClass isn't, just Category? Why?
        switch (activeItem.entityLevel) {
          case EntityLevel.Category:
            dispatch('setCategoryActive', { category: item })
            break
          case EntityLevel.ProductClass:
            dispatch('setProductClassActive', {
              category: rootActiveTopology.category,
              productClass: item,
            })
            break
          case EntityLevel.ManufacturingSeries:
            dispatch('setManufacturingSeriesActive', {
              category: rootActiveTopology.category,
              productClass: rootActiveTopology.productClass,
              manufacturingSeries: item,
            })
            break
          case EntityLevel.Product:
            dispatch('setProductActive', {
              category: rootActiveTopology.category,
              productClass: rootActiveTopology.productClass,
              manufacturingSeries: rootActiveTopology.manufacturingSeries,
              product: item,
            })
            break
        }
        dispatch('setTreeLoading', { value: false })
      })
      .catch(() => {
        commit('treeTopologyError')
      })
  },
  setTreeLoading({ commit }, { value }: { value: boolean }) {
    return new Promise<void>((resolve) => {
      commit('setTreeLoading', { value })
      resolve()
    })
  },
  toggleCategoryOpen({ commit, state }, { category }: { category: Category }) {
    const { categoryOpen } = state
    commit('setCategoryOpen', {
      category,
      value: !categoryOpen[category.CategoryId],
    })
  },
  toggleProductClassOpen: (
    { commit, state },
    { productClass }: { productClass: ProductClass },
  ) => {
    const { productClassOpen } = state
    commit('setProductClassOpen', {
      productClass,
      value: !productClassOpen[productClass.ProductClassId],
    })
  },
  toggleManufacturingSeriesOpen: (
    { commit, state },
    { manufacturingSeries }: { manufacturingSeries: ManufacturingSeries },
  ) => {
    const { manufacturingSeriesOpen } = state
    commit('setManufacturingSeriesOpen', {
      manufacturingSeries,
      value:
        !manufacturingSeriesOpen[manufacturingSeries.ManufacturingSeriesId],
    })
  },
  setProductActive({ commit }, { product }: Topology) {
    commit('setActiveItem', { item: product, entityLevel: EntityLevel.Product })
  },
  setProductSectionsLoading({ commit }, loading) {
    commit('productSectionsLoadingSet', loading)
  },
  setCategoryActive(
    { commit, dispatch },
    { category }: { category: Category },
  ) {
    dispatch('setActiveTopology', { category })
    commit('setActiveItem', {
      item: category,
      entityLevel: EntityLevel.Category,
    })
  },
  resetCategoriesOpen({ commit }) {
    commit('categoriesOpenReset')
  },
}
